import { useState, useEffect } from 'react';
import './ConviteEvento.css';
import CalrizLogo from '../../assets/logos/logo-oficial-calriz.png';
import GoogleCloudLogo from '../../assets/logos/google-cloud.png';
import Seo from '../../seo/Seo';

const REGISTRATION_URL = 'https://backend.calriz.com/api/registro-evento';
const EVENT_DATE = new Date('2026-06-10T08:00:00-03:00');

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function calcTimeLeft(): TimeLeft {
  const diff = EVENT_DATE.getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

function maskTelefone(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function isValidTelefone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length === 10 || digits.length === 11;
}

export default function Registration() {
  const [step, setStep] = useState(1);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calcTimeLeft);
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [organizacao, setOrganizacao] = useState('');
  const [cargo, setCargo] = useState('');
  const [telefone, setTelefone] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; nome?: string; telefone?: string }>({});

  useEffect(() => {
    const interval = setInterval(() => setTimeLeft(calcTimeLeft()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (submitted) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [submitted]);

  async function handleSubmit() {
    if (!isValidTelefone(telefone)) {
      setErrors(e => ({ ...e, telefone: 'Telefone inválido. Informe DDD + número completo.' }));
      return;
    }
    const payload = { email, nome, organizacao, cargo, telefone, cidade: 'goiania' };
    if (!REGISTRATION_URL) {
      console.log('Registration payload:', payload);
      setSubmitted(true);
      return;
    }
    try {
      const res = await fetch(REGISTRATION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Network response not ok');
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setErrors(e => ({ ...e, telefone: 'Erro ao enviar inscrição. Tente novamente.' }));
    }
  }

  function handleNextEmail() {
    if (!email.trim()) {
      setErrors(e => ({ ...e, email: 'Preencha o e-mail.' }));
      return;
    }
    if (!isValidEmail(email)) {
      setErrors(e => ({ ...e, email: 'E-mail inválido. Verifique o formato.' }));
      return;
    }
    setErrors(e => ({ ...e, email: undefined }));
    setStep(2);
  }

  function handleNextNome() {
    if (!nome.trim()) {
      setErrors(e => ({ ...e, nome: 'Preencha o nome completo.' }));
      return;
    }
    setErrors(e => ({ ...e, nome: undefined }));
    setStep(3);
  }

  function stepClass(n: number) {
    if (n === step) return 'reg-step reg-step--active';
    if (n < step) return 'reg-step reg-step--done';
    return 'reg-step';
  }

  if (submitted) {
    return (
      <div className="rsvp-page reg-page">
        <nav className="rsvp-nav">
          <div className="rsvp-nav-inner">
            <div className="rsvp-nav-left">
              <img src={CalrizLogo} alt="Calriz" className="rsvp-nav-calriz-logo" />
              <span className="rsvp-nav-divider" />
              <img src={GoogleCloudLogo} alt="Google Cloud" className="rsvp-nav-gc-logo" />
            </div>
          </div>
        </nav>
        <div className="reg-container">
          <div className="reg-success">
            <svg className="reg-success-icon" viewBox="0 0 24 24" fill="none" stroke="#1a73e8" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="9 12 11 14 15 10" />
            </svg>
            <h2>Inscrição confirmada!</h2>
            <p>Obrigado! Você receberá um e-mail de confirmação em breve.</p>
            <a href="/best-of-next-goiania" className="rsvp-btn-pill">Voltar ao evento</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rsvp-page reg-page">
      <Seo title="Inscrição – O melhor do Next Goiânia" description="Impulsione a era agêntica com as novidades do Google Cloud 2026: evento presencial da Calriz em Goiânia. Faça sua inscrição." path="/best-of-next-goiania/inscricao" noindex />
{/* Navbar */}
      <nav className="rsvp-nav">
        <div className="rsvp-nav-inner">
          <div className="rsvp-nav-left">
            <img src={CalrizLogo} alt="Calriz" className="rsvp-nav-calriz-logo" />
            <img src={GoogleCloudLogo} alt="Google Cloud" className="rsvp-nav-gc-logo" />
            <ul className="rsvp-nav-links">
              <li>
                <a href="#" className="rsvp-nav-link rsvp-nav-link--active">Home</a>
              </li>
            </ul>
          </div>

          <div className="rsvp-nav-right">
            <span className="rsvp-countdown">
              Começa em{' '}
              <strong>
                {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
              </strong>
            </span>
          </div>
        </div>
      </nav>

      <div className="reg-container">

        {/* Header */}
        <div className="reg-header">
          <h1 className="reg-event-title">O melhor do Next</h1>
          <div className="reg-event-date">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="reg-date-icon">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span>10 de junho de 2026 08:00 – 12:00 BRT</span>
          </div>
        </div>

        {/* Step 1: E-mail */}
        <div className={stepClass(1)}>
          <div className="reg-step-number">1</div>
          <div className="reg-step-body">
            <div className="reg-step-title">E-mail</div>
            {step === 1 && (
              <div className="reg-step-content">
                <input
                  type="email"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors(ev => ({ ...ev, email: undefined }));
                  }}
                  onKeyDown={e => e.key === 'Enter' && handleNextEmail()}
                  placeholder="Insira o e-mail"
                  className={`rsvp-input reg-email-input${errors.email ? ' reg-input--error' : ''}`}
                  maxLength={80}
                  autoFocus
                />
                {errors.email && <p className="reg-field-error">{errors.email}</p>}
                <div className="reg-step-footer">
                  <a href="/best-of-next-goiania" className="reg-already-link">
                    Já é inscrito? Gerencie sua inscrição.
                  </a>
                  <button
                    type="button"
                    style={{ cursor: 'pointer' }}
                    className="rsvp-btn-pill"
                    onClick={handleNextEmail}
                  >
                    Próximo
                  </button>
                </div>
              </div>
            )}
            {step > 1 && <div className="reg-step-summary">{email}</div>}
          </div>
        </div>

        {/* Step 2: Nome */}
        <div className={stepClass(2)}>
          <div className="reg-step-number">2</div>
          <div className="reg-step-body">
            <div className="reg-step-title">Nome</div>
            {step === 2 && (
              <div className="reg-step-content">
                <input
                  type="text"
                  value={nome}
                  onChange={e => {
                    setNome(e.target.value);
                    if (errors.nome) setErrors(ev => ({ ...ev, nome: undefined }));
                  }}
                  onKeyDown={e => e.key === 'Enter' && handleNextNome()}
                  placeholder="Nome completo"
                  className={`rsvp-input reg-email-input${errors.nome ? ' reg-input--error' : ''}`}
                  maxLength={80}
                  autoFocus
                />
                {errors.nome && <p className="reg-field-error">{errors.nome}</p>}
                <div className="reg-step-footer">
                  <button type="button" className="reg-btn-back" onClick={() => setStep(1)}>Voltar</button>
                  <button
                    type="button"
                    style={{ cursor: 'pointer' }}
                    className="rsvp-btn-pill"
                    onClick={handleNextNome}
                  >
                    Próximo
                  </button>
                </div>
              </div>
            )}
            {step > 2 && <div className="reg-step-summary">{nome}</div>}
          </div>
        </div>

        {/* Step 3: Organização */}
        <div className={stepClass(3)}>
          <div className="reg-step-number">3</div>
          <div className="reg-step-body">
            <div className="reg-step-title">Organização</div>
            {step === 3 && (
              <div className="reg-step-content">
                <input
                  type="text"
                  value={organizacao}
                  onChange={e => setOrganizacao(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && setStep(4)}
                  placeholder="Empresa ou organização"
                  className="rsvp-input reg-email-input"
                  maxLength={80}
                  autoFocus
                />
                <div className="reg-step-footer">
                  <button type="button" className="reg-btn-back" onClick={() => setStep(2)}>Voltar</button>
                  <button
                    type="button"
                    style={{ cursor: 'pointer' }}
                    className="rsvp-btn-pill"
                    onClick={() => setStep(4)}
                  >
                    Próximo
                  </button>
                </div>
              </div>
            )}
            {step > 3 && <div className="reg-step-summary">{organizacao || '—'}</div>}
          </div>
        </div>

        {/* Step 4: Cargo */}
        <div className={stepClass(4)}>
          <div className="reg-step-number">4</div>
          <div className="reg-step-body">
            <div className="reg-step-title">Cargo</div>
            {step === 4 && (
              <div className="reg-step-content">
                <input
                  type="text"
                  value={cargo}
                  onChange={e => setCargo(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && setStep(5)}
                  placeholder="Cargo ou função"
                  className="rsvp-input reg-email-input"
                  maxLength={80}
                  autoFocus
                />
                <div className="reg-step-footer">
                  <button type="button" className="reg-btn-back" onClick={() => setStep(3)}>Voltar</button>
                  <button
                    type="button"
                    style={{ cursor: 'pointer' }}
                    className="rsvp-btn-pill"
                    onClick={() => setStep(5)}
                  >
                    Próximo
                  </button>
                </div>
              </div>
            )}
            {step > 4 && <div className="reg-step-summary">{cargo || '—'}</div>}
          </div>
        </div>

        {/* Step 5: Telefone + Submit */}
        <div className={stepClass(5)}>
          <div className="reg-step-number">5</div>
          <div className="reg-step-body">
            <div className="reg-step-title">Telefone</div>
            {step === 5 && (
              <div className="reg-step-content">
                <input
                  type="tel"
                  value={telefone}
                  onChange={e => {
                    setTelefone(maskTelefone(e.target.value));
                    if (errors.telefone) setErrors(ev => ({ ...ev, telefone: undefined }));
                  }}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                  placeholder="(00) 00000-0000"
                  className={`rsvp-input reg-email-input${errors.telefone ? ' reg-input--error' : ''}`}
                  autoFocus
                />
                {errors.telefone && <p className="reg-field-error">{errors.telefone}</p>}
                <div className="reg-step-footer">
                  <button type="button" className="reg-btn-back" onClick={() => setStep(4)}>Voltar</button>
                  <button
                    type="button"
                    style={{ cursor: 'pointer' }}
                    className="rsvp-btn-pill"
                    onClick={handleSubmit}
                  >
                    Enviar inscrição
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
